import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnderaiterMark, LogoIcon } from '@/components/brand/Marks';
import { Play, Pause, Volume2, VolumeX, AlertCircle } from 'lucide-react';

/**
 * Shared demo player used by all demo pages (and the landing-page modal when `embedded`).
 * The narration drives the clock: slides follow the audio's own time, so picture and voice never
 * drift apart. If the audio cannot load or play, the player says so and runs on a timer with the
 * captions — it never sits silent on the first frame.
 */
export default function DemoPlayer({ slides, totalDuration, audioSrc, accentColor = '#00B4D8', badge = 'Demo', browserTitle = 'underaiter.com', navLinks = [], posterImage = null, embedded = false, autoPlay = false, startAt = 0, controlsRef = null, onSlideChange = null }) {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [audioState, setAudioState] = useState('loading');   // loading | ready | failed
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(startAt);

  const slideAt = useCallback((t) => {
    for (let i = slides.length - 1; i >= 0; i--) { if (t >= slides[i].time) return i; }
    return 0;
  }, [slides]);

  const setClock = useCallback((t) => {
    const clamped = Math.min(Math.max(t, 0), totalDuration);
    elapsedRef.current = clamped;
    setElapsed(clamped);
    setCurrentSlide(slideAt(clamped));
  }, [slideAt, totalDuration]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const finish = useCallback(() => {
    stopTimer();
    setPlaying(false);
    setClock(totalDuration);
  }, [setClock, totalDuration]);

  // Fallback clock — only while the narration is unavailable
  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      const next = elapsedRef.current + 0.5;
      if (next >= totalDuration) { finish(); return; }
      setClock(next);
    }, 500);
  }, [finish, setClock, totalDuration]);

  useEffect(() => {
    const audio = new Audio(audioSrc);
    audio.preload = 'auto';
    audioRef.current = audio;
    const onReady = () => setAudioState('ready');
    const onError = () => setAudioState('failed');
    const onTime = () => { if (!audio.paused) setClock(audio.currentTime); };
    const onEnded = () => finish();
    audio.addEventListener('canplay', onReady);
    audio.addEventListener('error', onError);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.pause();
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audioRef.current = null;
      stopTimer();
    };
  }, [audioSrc, finish, setClock]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  const play = useCallback(() => {
    const from = elapsedRef.current >= totalDuration ? 0 : elapsedRef.current;
    setClock(from);
    setPlaying(true);
    setStarted(true);
    const audio = audioRef.current;
    if (audio && audioState !== 'failed') {
      try { audio.currentTime = from; } catch (err) { /* metadata not ready yet — play() seeks on load */ }
      audio.play().then(() => stopTimer()).catch(err => {
        if (err?.name === 'AbortError') return;          // interrupted by pause/unmount — not a failure
        if (err?.name === 'NotAllowedError') {       // browser wants a tap first — show the play button
          stopTimer();
          setPlaying(false);
          setStarted(false);
          setClock(from);
          return;
        }
        console.error('Audio error:', err?.message);
        setAudioState('failed');
        startTimer();
      });
      // Until the audio actually advances, keep the picture moving
      startTimer();
    } else {
      startTimer();
    }
  }, [audioState, setClock, startTimer, totalDuration]);

  const pause = () => { setPlaying(false); if (audioRef.current) audioRef.current.pause(); stopTimer(); };
  const replay = () => { setClock(0); play(); };
  const seekTo = (t) => {
    setClock(t);
    if (audioRef.current && audioState !== 'failed') { try { audioRef.current.currentTime = t; } catch (err) { /* not seekable yet */ } }
  };
  const goToSlide = (idx) => seekTo(slides[idx].time);

  // Keep the fallback timer off once the narration is heard advancing
  useEffect(() => {
    if (playing && audioState === 'ready' && audioRef.current && !audioRef.current.paused) stopTimer();
  }, [playing, audioState, elapsed]);

  useEffect(() => { setClock(startAt); if (autoPlay) play(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (onSlideChange) onSlideChange(currentSlide); }, [currentSlide, onSlideChange]);

  // Lets a parent (chapter list) drive the player
  useEffect(() => {
    if (!controlsRef) return undefined;
    controlsRef.current = { seekTo: (t) => { seekTo(t); if (!playing) play(); }, play, pause };
    return () => { controlsRef.current = null; };
  });

  const slide = slides[currentSlide];
  const pct = (elapsed / totalDuration) * 100;
  const mm = Math.floor(elapsed / 60);
  const ss = Math.floor(elapsed % 60);
  const ended = !playing && elapsed >= totalDuration;
  const showOverlay = !playing && !started && !ended;
  const showPoster = posterImage && !started && startAt === 0;

  const frame = (
    <div className={`relative rounded-lg overflow-hidden shadow-xl min-h-0 ${embedded ? 'w-full' : 'flex-1'}`} style={{ border: '1px solid #D0D5DD', ...(embedded ? { aspectRatio: '16 / 9', background: '#0D1B2A' } : {}) }}>
      <div className="flex items-center gap-2 px-3 py-1" style={{ background: '#F7F8FA', borderBottom: '1px solid #E4E7EC' }}>
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full" style={{ background: '#E24B4A' }} />
          <div className="w-2 h-2 rounded-full" style={{ background: '#E8A020' }} />
          <div className="w-2 h-2 rounded-full" style={{ background: '#1D9E75' }} />
        </div>
        <div className="flex-1 text-center text-[9px] font-mono" style={{ color: '#9CA3AF' }}>{browserTitle}</div>
        {audioState === 'failed' && (
          <span className="flex items-center gap-1 text-[9px] font-medium" style={{ color: '#B3261E' }} data-testid="demo-audio-unavailable">
            <AlertCircle className="w-3 h-3" /> narration unavailable — captions on
          </span>
        )}
      </div>

      <div className="relative h-[calc(100%-24px)]">
        <img src={showPoster ? posterImage : slide.image} alt={slide.title} className="w-full h-full object-cover object-top" />

        {showOverlay && (
          <button onClick={play} className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/10 transition-colors" data-testid="demo-play-overlay" aria-label="Play the demo">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105" style={{ background: accentColor }}>
              <Play className="w-6 h-6 ml-1" />
            </div>
          </button>
        )}

        {ended && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="demo-end-card">
            <div className="text-center px-6">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Want to see it live on your book?</p>
              <p className="text-sm mt-2 text-white/70">Get a personalised walkthrough with your own submissions.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <a href="/login?mode=waitlist" data-testid="demo-end-request"
                  className="px-7 py-3 rounded-full text-sm font-bold text-white transition-transform hover:scale-105" style={{ background: '#FF9933' }}>
                  Request a Demo
                </a>
                <button onClick={replay} data-testid="demo-end-replay"
                  className="px-7 py-3 rounded-full text-sm font-semibold text-white transition-colors hover:bg-white/10" style={{ border: '1.5px solid rgba(255,255,255,0.4)' }}>
                  Replay
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
          <div className="px-5 pt-8 pb-2">
            <p className="text-[11px] uppercase tracking-wider font-bold" style={{ color: accentColor }} data-testid="demo-slide-title">
              {String(currentSlide + 1).padStart(2, '0')} / {slides.length} — {slide.title}
            </p>
            <p className="text-[14px] mt-1 leading-relaxed max-w-3xl font-medium" style={{ color: '#FFFFFF' }} data-testid="demo-narration">{slide.narration}</p>
          </div>

          <div className="px-5 pb-1">
            <div className="relative h-1 rounded-full overflow-hidden cursor-pointer" style={{ background: 'rgba(255,255,255,0.2)' }} data-testid="demo-progress"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                seekTo(((e.clientX - rect.left) / rect.width) * totalDuration);
              }}>
              {slides.map(s => <div key={s.id} className="absolute top-0 w-px h-full" style={{ left: `${(s.time / totalDuration) * 100}%`, background: 'rgba(255,255,255,0.3)' }} />)}
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accentColor }} />
            </div>
          </div>

          <div className="flex items-center justify-between px-5 pb-3 pt-1">
            <div className="flex items-center gap-2.5">
              <button data-testid="demo-play-btn" onClick={playing ? pause : play} aria-label={playing ? 'Pause' : 'Play'}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105" style={{ background: accentColor }}>
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
              </button>
              <button onClick={() => setMuted(!muted)} className="p-1 rounded-full hover:bg-white/10" data-testid="demo-mute-btn" aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX className="w-3.5 h-3.5 text-white/50" /> : <Volume2 className="w-3.5 h-3.5 text-white/80" />}
              </button>
              <span className="text-[11px] font-mono text-white/60" data-testid="demo-clock">
                {mm}:{String(ss).padStart(2, '0')} / {Math.floor(totalDuration / 60)}:{String(Math.floor(totalDuration % 60)).padStart(2, '0')}
              </span>
            </div>
            <div className="flex gap-1">
              {slides.map((s, i) => (
                <button key={s.id} onClick={() => goToSlide(i)} title={s.title} aria-label={s.title}
                  className={`h-1 rounded-full transition-all ${i === currentSlide ? 'w-5' : 'w-1'}`}
                  style={{ background: i === currentSlide ? accentColor : i < currentSlide ? '#1D9E75' : 'rgba(255,255,255,0.3)' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) return <div data-testid="demo-player">{frame}</div>;

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#F0F2F5' }} data-testid="demo-player">
      <nav className="h-11 px-5 flex items-center justify-between border-b shrink-0 bg-white" style={{ borderColor: '#E4E7EC' }}>
        <div className="flex items-center gap-2.5">
          <LogoIcon size={22} />
          <UnderaiterMark size="text-sm" />
          <span className="text-[10px] px-2 py-0.5 rounded-sm ml-1.5 font-semibold" style={{ background: accentColor + '15', color: accentColor }}>{badge}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {navLinks.map(link => (
            <button key={link.path} onClick={() => navigate(link.path)} className="text-[11px] px-3 py-1 rounded-sm transition-colors hover:bg-gray-50"
              style={{ color: link.color || '#5C6B7A', border: `1px solid ${(link.color || '#5C6B7A') + '30'}` }}>
              {link.label}
            </button>
          ))}
          <button onClick={() => navigate('/login?mode=waitlist')} className="text-[11px] px-3 py-1 rounded-sm font-semibold text-white" style={{ background: accentColor }}>Request Demo</button>
          <button onClick={() => navigate('/login')} className="text-[11px] px-3 py-1 rounded-sm" style={{ color: '#5C6B7A' }}>Sign In</button>
        </div>
      </nav>
      <div className="flex-1 flex flex-col min-h-0 p-3">{frame}</div>
    </div>
  );
}
